import { useState } from "react";
import { Hand, KeyRound, MessageCircleQuestion } from "lucide-react";
import { arr, bool, obj, str, strings, methodNotFound, type JsonObject, type ServerRequest } from "@timibot/core";
import type { ChatStore } from "../../state/chat";
import { Markdown } from "./Markdown";
import "./chat.css";

/**
 * 服务端→客户端请求的交互卡片（对应 iOS 的 InteractionCard）：批准命令、澄清问题、sudo/密钥输入。
 */
export function InteractionCard({ request, chat }: { request: ServerRequest; chat: ChatStore }) {
  let body;
  switch (request.method) {
    case "approval": body = <ApprovalCard request={request} chat={chat} />; break;
    case "clarify": body = <ClarifyCard request={request} chat={chat} />; break;
    case "sudo": case "secret": body = <SecretCard request={request} chat={chat} />; break;
    default:
      body = (
        <div className="row t-footnote" style={{ gap: 8 }}>
          <span style={{ flex: 1 }}>网页端暂不支持的请求：{request.method}</span>
          <button className="pill-button" onClick={() => chat.resolve(request, methodNotFound)}>忽略</button>
        </div>
      );
  }
  return <div className="interaction-card">{body}</div>;
}

function ApprovalCard({ request, chat }: { request: ServerRequest; chat: ChatStore }) {
  const p = obj(request.params) ?? {};
  const choices = strings(p.choices) ?? ["once", "session", "always", "deny"];
  const answer = (choice: string) => chat.resolve(request, { result: { choice } });
  const description = str(p.description);
  const command = str(p.command);
  return (
    <div className="col" style={{ gap: 10 }}>
      <span className="card-label t-subheadline semibold" style={{ color: "var(--warning)" }}><Hand size={16} /> 需要你批准</span>
      {description && <span className="t-footnote t-2">{description}</span>}
      {command && <pre className="approval-command mono t-footnote">{command}</pre>}
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {choices.includes("once") && <button className="choice-button prominent" onClick={() => answer("once")}>允许一次</button>}
        {choices.includes("session") && <button className="choice-button" onClick={() => answer("session")}>本会话允许</button>}
        <span className="spacer" />
        <button className="choice-button danger" onClick={() => answer("deny")}>拒绝</button>
      </div>
      {choices.includes("always") && (
        <button className="t-caption t-2" style={{ alignSelf: "flex-start" }} onClick={() => answer("always")}>总是允许这类命令</button>
      )}
    </div>
  );
}

interface Question { id: string; text: string; choices: string[]; multi: boolean }

function ClarifyCard({ request, chat }: { request: ServerRequest; chat: ChatStore }) {
  const p = obj(request.params) ?? {};
  const batch = arr(p.questions);
  const questions: Question[] = batch && batch.length
    ? batch.map(obj).filter(Boolean).flatMap((q) => {
        const id = str(q!.qid);
        const text = str(q!.question);
        return id && text ? [{ id, text, choices: strings(q!.choices) ?? [], multi: bool(q!.multi_select) ?? false }] : [];
      })
    : [{ id: "_single", text: str(p.question) ?? "需要你补充信息", choices: strings(p.choices) ?? [], multi: bool(p.multi_select) ?? false }];
  const [freeText, setFreeText] = useState("");
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const single = questions.length === 1;
  // 带 questions 数组的是“批量”请求（哪怕只有一题）：Hermes 只认 {"answers": {qid: 答案}}，回 {"answer"} 会被当成没回答
  const isBatch = !!batch && batch.length > 0;

  const submitSingle = (answer: string) => {
    const a = answer.trim();
    if (!a) return;
    chat.resolve(request, isBatch ? { result: { answers: { [questions[0].id]: a } } } : { result: { answer: a } });
  };

  const tap = (q: Question, choice: string) => {
    if (q.multi || !single) {
      const set = selections[q.id] ?? [];
      const next = q.multi ? (set.includes(choice) ? set.filter((c) => c !== choice) : [...set, choice]) : [choice];
      setSelections({ ...selections, [q.id]: next });
    } else {
      submitSingle(choice);
    }
  };

  const submit = () => {
    if (single) {
      const q = questions[0];
      const set = selections[q.id] ?? [];
      if (q.multi && set.length) submitSingle([...q.choices.filter((c) => set.includes(c)), ...(freeText ? [freeText] : [])].join(", "));
      else submitSingle(freeText);
      return;
    }
    const answers: JsonObject = {};
    for (const q of questions) {
      const picked = q.choices.filter((c) => (selections[q.id] ?? []).includes(c));
      const typed = (texts[q.id] ?? "").trim();
      answers[q.id] = [...picked, ...(typed ? [typed] : [])].join(", ");
    }
    chat.resolve(request, { result: { answers } });
  };

  const canSubmit = single ? (freeText.trim().length > 0 || (questions[0].multi && (selections[questions[0].id] ?? []).length > 0)) : true;

  return (
    <div className="col" style={{ gap: 12 }}>
      <span className="card-label t-subheadline semibold t-brand"><MessageCircleQuestion size={16} /> 需要你回答</span>
      {questions.map((q) => (
        <div key={q.id} className="col" style={{ gap: 8 }}>
          <Markdown text={q.text} small />
          {q.choices.length > 0 && (
            <div className="choice-flow">
              {q.choices.map((c) => (
                <button key={c} className={`choice-button${(selections[q.id] ?? []).includes(c) ? " prominent" : ""}`} onClick={() => tap(q, c)}>{c}</button>
              ))}
            </div>
          )}
          {!single && (
            <input className="card-input" placeholder="或者直接输入" value={texts[q.id] ?? ""} onChange={(e) => setTexts({ ...texts, [q.id]: e.target.value })} />
          )}
        </div>
      ))}
      <div className="row" style={{ gap: 8 }}>
        {single && (
          <input
            className="card-input"
            style={{ flex: 1 }}
            placeholder={questions[0].choices.length ? "其他回答…" : "输入回答…"}
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) submitSingle(freeText); }}
          />
        )}
        <button className="choice-button prominent" disabled={!canSubmit} onClick={submit}>
          {single && !questions[0].multi ? "发送" : "提交"}
        </button>
      </div>
      <button className="t-caption t-2" style={{ alignSelf: "flex-start" }} onClick={() => chat.resolve(request, { error: { code: -32000, message: "skipped" } })}>跳过</button>
    </div>
  );
}

function SecretCard({ request, chat }: { request: ServerRequest; chat: ChatStore }) {
  const p = obj(request.params) ?? {};
  const [value, setValue] = useState("");
  const prompt = str(p.prompt) ?? str(p.command);
  const env = str(p.env_var);
  const submit = () => {
    if (!value) return;
    const v = value;
    setValue("");
    chat.resolve(request, { result: { value: v } });
  };
  return (
    <div className="col" style={{ gap: 10 }}>
      <span className="card-label t-subheadline semibold" style={{ color: "var(--warning)" }}>
        <KeyRound size={16} /> {request.method === "sudo" ? "需要 sudo 密码" : "需要填写密钥"}
      </span>
      {prompt && <span className="t-footnote t-2">{prompt}</span>}
      {env && <span className="t-caption mono t-3">{env}</span>}
      <input
        className="card-input"
        type="password"
        autoComplete="off"
        placeholder="输入后仅发送给你自己的 Hermes"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
      />
      <div className="row">
        <button className="choice-button" onClick={() => chat.resolve(request, { error: { code: -32000, message: "declined" } })}>取消</button>
        <span className="spacer" />
        <button className="choice-button prominent" disabled={!value} onClick={submit}>提交</button>
      </div>
    </div>
  );
}
